import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { Router } from '@angular/router'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Store } from '@ngxs/store'
import {
  AnnouncementsState,
  IAnnouncementItem,
  MarkAnnouncementRead
} from '@lla-platform/announcements/announcements-data-access'
import { FullScreenModalConfig, MobileMaxWidth, NormalModalConfig } from '@lla-platform/core/core-util'
import { LoudAnnouncementDialogComponent } from '../loud-announcement-dialog/loud-announcement-dialog.component'

// Renderless host: watches the store's pending loud announcements (patched from every
// unread-count poll) and shows them one dialog at a time, oldest first. Closing marks the
// item read, which removes it from the store — that re-emission opens the next pending item.
@UntilDestroy()
@Component({
  selector: 'lla-loud-announcements-host',
  template: '',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class LoudAnnouncementsHostComponent implements OnInit {
  loudAnnouncements$ = inject(Store).select(AnnouncementsState.loudAnnouncements)

  // Session-lived: stops an item from re-popping while polls still return it (mark-read
  // in flight or failed — after a reload a failed mark-read pops again).
  private shownIds = new Set<string>()
  private dialogOpen = false
  private latestItems: IAnnouncementItem[] = []

  constructor(
    private store: Store,
    private dialogService: MatDialog,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loudAnnouncements$.pipe(untilDestroyed(this)).subscribe((items) => {
      this.latestItems = items ?? []
      this.maybeOpenNext(this.latestItems)
    })
  }

  private maybeOpenNext(items: IAnnouncementItem[]) {
    if (this.dialogOpen) {
      return
    }
    const item = items.find((q) => !this.shownIds.has(q.recipientId))
    if (!item) {
      return
    }
    this.shownIds.add(item.recipientId)
    this.dialogOpen = true
    const config =
      window.innerWidth < MobileMaxWidth
        ? { ...FullScreenModalConfig }
        : {
            ...NormalModalConfig,
            width: '50vw',
            minWidth: 'min(640px, 92vw)',
            maxWidth: '960px',
            maxHeight: '85vh'
          }
    this.dialogService
      .open(LoudAnnouncementDialogComponent, {
        ...config,
        data: { item }
      })
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe((result) => {
        this.dialogOpen = false
        this.store.dispatch(new MarkAnnouncementRead(item.recipientId))
        if (result === 'view') {
          this.router.navigate(['/announcements', item.announcementId])
        }
        // Open the next pending item immediately
        this.maybeOpenNext(this.latestItems)
      })
  }
}
